import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { LayoutGrid, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import type { CategoriaTarifaria, ListaDeTarifas } from '@/shared/api/tipos';
import { formatearTasa } from './nombres';
import { InsigniaActiva } from './InsigniaActiva';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { PestanasDelTarifario } from './PestanasDelTarifario';

const esquema = z.object({
  nombre: z.string().min(1, 'El nombre es obligatorio').max(120),
  codigo: z
    .string()
    .min(1, 'El código es obligatorio')
    .max(60)
    .regex(/^[a-z0-9_]+$/, 'Solo minúsculas, números y guion bajo'),
  orden: z.coerce.number().int().min(0),
});

type Formulario = z.infer<typeof esquema>;

export function PaginaCategoriasTarifarias() {
  const clienteDeConsultas = useQueryClient();

  const [editando, setEditando] = useState<CategoriaTarifaria | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<CategoriaTarifaria | null>(null);
  const [error, setError] = useState<string | null>(null);

  const categorias = useQuery({
    queryKey: ['categorias-tarifarias'],
    queryFn: () => api.get<CategoriaTarifaria[]>('/categorias-tarifarias'),
  });

  const tarifas = useQuery({
    queryKey: ['tarifas'],
    queryFn: () => api.get<ListaDeTarifas>('/tarifas'),
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['categorias-tarifarias'] });
  }

  const cambiarEstado = useMutation({
    mutationFn: (c: CategoriaTarifaria) =>
      api.put<CategoriaTarifaria>(`/categorias-tarifarias/${c.id}`, {
        nombre: c.nombre,
        codigo: c.codigo,
        orden: c.orden,
        activa: !c.activa,
      }),
    onSuccess: refrescar,
    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el estado';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/categorias-tarifarias/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Categoría eliminada');
    },
    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo eliminar';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  return (
    <>
      <EncabezadoDePagina
        icono={LayoutGrid}
        color="ambar"
        titulo="Categorías tarifarias"
        subtitulo="Cómo se agrupan las tarifas"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nueva categoría"
            aria-label="Nueva categoría"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      <PestanasDelTarifario />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
        {categorias.isPending ? (
          <CargandoEnLinea detalle="Obteniendo registros" />
        ) : categorias.data?.length === 0 ? (
          <div className="p-16 text-center">
            <LayoutGrid className="mx-auto mb-3 size-12 text-armazon-200" aria-hidden />
            <p className="text-sm font-medium uppercase text-armazon-500">No hay categorías</p>
          </div>
        ) : (
          <div className="divide-y divide-armazon-100">
            {categorias.data?.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center gap-4 px-6 py-4 transition-colors hover:bg-armazon-50/60"
              >
                <span className="importe grid size-8 shrink-0 place-items-center rounded-lg bg-armazon-100 text-xs font-semibold text-armazon-600">
                  {c.orden}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium uppercase text-armazon-800">
                    {c.nombre}
                  </p>
                  <ResumenDeCategoria
                    categoria={c}
                    tarifas={tarifas.data?.tarifas}
                    cargando={tarifas.isPending}
                  />
                </div>

                <InsigniaActiva
                  activa={c.activa}
                  alAlternar={() => cambiarEstado.mutate(c)}
                  nombre={c.nombre}
                  cambiando={cambiarEstado.isPending}
                />

                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditando(c)}
                    title="Editar"
                    aria-label={`Editar ${c.nombre}`}
                    className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Pencil className="size-[15px]" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => setAEliminar(c)}
                    title="Eliminar"
                    aria-label={`Eliminar ${c.nombre}`}
                    className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
                  >
                    <Trash2 className="size-[15px]" aria-hidden />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <FormularioDeCategoria
        key={editando?.id ?? 'nueva'}
        abierto={creando || editando !== null}
        categoria={editando}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => {
          setAEliminar(null);
          setError(null);
        }}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar categoría"
        descripcion="Dejará de poder agruparse por:"
        detalle={aEliminar?.nombre}
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

export function ResumenDeCategoria({
  categoria,
  tarifas,
  cargando,
}: {
  categoria: CategoriaTarifaria;
  tarifas: ListaDeTarifas['tarifas'] | undefined;
  cargando: boolean;
}) {
  if (cargando || !tarifas) {
    return <p className="h-4 w-40 animate-pulse rounded bg-armazon-100" />;
  }

  const suyas = tarifas.filter((t) => t.categoria === categoria.nombre);

  if (suyas.length === 0) {
    return (
      <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-amber-700">
        Sin tarifas · a esas cuentas se les cobraría cero
      </p>
    );
  }

  const tasas = suyas.filter((t) => t.tasa != null).map((t) => (t.tasa ?? 0) * 100);
  const cuantas = `${suyas.length} ${suyas.length === 1 ? 'tarifa' : 'tarifas'}`;

  if (tasas.length === 0) {
    return (
      <p className="truncate text-[11px] uppercase tracking-wide text-armazon-500">
        {cuantas} · valor fijo
      </p>
    );
  }

  const menor = Math.min(...tasas);
  const mayor = Math.max(...tasas);
  const rango =
    menor === mayor
      ? `${formatearTasa(menor)} %`
      : `${formatearTasa(menor)} % – ${formatearTasa(mayor)} %`;

  return (
    <p className="truncate text-[11px] uppercase tracking-wide text-armazon-500">
      {cuantas} · <span className="importe font-medium text-indigo-600">{rango}</span> al mes
    </p>
  );
}

function FormularioDeCategoria({
  abierto,
  categoria,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  categoria: CategoriaTarifaria | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = categoria !== null;
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: {
      nombre: categoria?.nombre ?? '',
      codigo: categoria?.codigo ?? '',
      orden: categoria?.orden ?? 0,
    },
  });

  const enviar = handleSubmit(async (datos) => {
    setErrorGeneral(null);
    try {
      const cuerpo = { ...datos, activa: categoria?.activa ?? true };

      if (editando) {
        await api.put<CategoriaTarifaria>(`/categorias-tarifarias/${categoria.id}`, cuerpo);
      } else {
        await api.post<CategoriaTarifaria>('/categorias-tarifarias', cuerpo);
      }

      alGuardar();
      alCerrar();
      notificar.exito(`Categoría ${editando ? 'actualizada' : 'guardada'}`);
    } catch (causa) {
      setErrorGeneral(
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar la categoría',
      );
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar la categoría',
      );
    }
  });

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar categoría' : 'Nueva categoría'}
      descripcion={editando ? categoria.codigo : 'Cómo se agrupan las tarifas'}
      icono={LayoutGrid}
      alEnviar={enviar}
      enviando={isSubmitting}
    >
      {errorGeneral && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {errorGeneral}
        </p>
      )}

      <CampoDelPanel
        etiqueta="Nombre"
        requerido
        error={errors.nombre?.message}
        ayuda="Es con lo que las tarifas se agrupan y buscan"
      >
        <input {...register('nombre')} className={claseEntradaPanel} autoFocus />
      </CampoDelPanel>

      <DosColumnas>
        <CampoDelPanel
          etiqueta="Código"
          requerido
          error={errors.codigo?.message}
          ayuda="Estable: el nombre se corrige sin romper nada"
        >
          <input {...register('codigo')} className={`${claseEntradaPanel} importe`} />
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Orden" error={errors.orden?.message} ayuda="Menor va primero">
          <input
            {...register('orden')}
            type="number"
            min={0}
            className={`${claseEntradaPanel} importe`}
          />
        </CampoDelPanel>
      </DosColumnas>
    </PanelLateral>
  );
}
